/* eslint-disable complexity */
import {
  AddressCard,
  Box,
  Checkbox,
  Divider,
  Grid,
  IconButton,
  TextField,
} from '@atgfr8/atg-ui-tailwind';
import { EyeIcon, EyeOffIcon } from '@atgfr8/icons';
import {
  Button,
  Notification,
  PhoneInput,
  SelectInput,
  ToastType,
} from '@components';
import { AtgLoader } from '@components/AtgLoader';
import { createForm } from '@felte/solid';
import { validator } from '@felte/validator-yup';
import { Contact as CarrierContactT } from '@store/carriers';
import {
  Contact as CustomerContactT,
  customerDetailsStore,
  getCustomerUserNameExists,
} from '@store/customers/customerDetails';
import { customerContactSchema } from '@common/order/validations';
import { closeModal } from '@store/modals';
import { carrierStore, orderStore } from '@store/orders';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { normalizeWhitespace } from '@utils/formUtils';
import { handleToast, userHasRole } from '@utils/utils';
import { debounce } from 'lodash';
import { Accessor, createSignal, onMount, Show } from 'solid-js';

import { classes } from './addNewContact.style';
import { CarrierTypes, CustomerTypes } from './constant';
import { ExistingContactObj } from './type';
import { contactSchema } from './validation';
// import {
//   userHasPermission,
//   userHasRole,
// } from '../carrier/stops/stopsEditor/utilFunctions';

export type ContactSourceType = 'customer' | 'carrier';

type Props = {
  modalId: string;
  editObject?: Accessor<CustomerContactT | CarrierContactT | null>;
  handleClose?: () => void;
  useVerified?: boolean;
  readOnly?: boolean;
  initiatePath:
    | 'CarrierDetails'
    | 'CustomerDetails'
    | 'OrderDetails'
    | 'FactoringCompanyDetails'
    | 'ProspectDetails';
  onSubmit: (
    values: Record<string, string | number | boolean | null>,
  ) => Promise<void> | void;
  contactSource?: ContactSourceType;
};

export type ContactFormValues = {
  id?: number;
  name: string;
  type: string;
  phone1: string;
  phoneVerified: boolean;
  extension1: string | null;
  phone2: string;
  extension2: string | null;
  fax: string | null;
  email: string;
  emailVerified: boolean;
  cpn: string;
  createdDate: string;
  uniqueId: number | null;
  objectType: string;
  verifiedDisabled: boolean;
  verificationStatus: number;
  userName: string | null;
  password: string | null;
  phone?: string;
  mailerEmails?: boolean;
  operationType?: string;
  objectId?: number;
};

const LOGIN_CONTACT_TYPES = ['Billing', 'Freight'];

function AddNewContact(props: Props) {
  const [showPassword, setShowPassword] = createSignal(false);
  const [isUserNameLoading, setIsUserNameLoading] =
    createSignal<boolean>(false);
  const [duplicateUserNameError, setDuplicateUserNameError] = createSignal<
    string | null
  >(null);
  const contact = props.editObject ? props.editObject() : undefined;
  const contactSource = () => props.contactSource ?? 'customer';
  const isReadOnly = () => props.readOnly ?? false;
  const isCustomerContact = () => props.initiatePath === 'CustomerDetails';
  const contactPhoneMaxDigits = () => (isCustomerContact() ? 20 : 10);

  const { form, errors, data, isSubmitting, setIsSubmitting, setData } =
    createForm<ContactFormValues>({
      initialValues: {
        name: '',
        type: '',
        phone1: '',
        phoneVerified: false,
        extension1: null,
        phone2: '',
        extension2: null,
        fax: null,
        email: '',
        emailVerified: false,
        cpn:
          carrierStore.carrier[orderStore.activeTab.index]
            ?.corporatePhoneNumber || '',
        createdDate: new Date().toISOString(),
        uniqueId: -1,
        objectType: '',
        verifiedDisabled: false,
        verificationStatus: 0,
        userName: null,
        password: null,
        mailerEmails: false,
      },
      extend: validator({
        schema: isCustomerContact() ? customerContactSchema : contactSchema,
      }),
      onSubmit: async (values) => {
        await handleFormSubmit(values);
      },
    });

  const isLoginContactType = (type: string) =>
    LOGIN_CONTACT_TYPES.includes(type);

  const validateLoginNameAvailability = async (
    value: string | null,
    contactId = data().id,
  ) => {
    const userName = value?.trim() ?? '';

    if (!userName) {
      setDuplicateUserNameError(null);
      return true;
    }

    const res = await getCustomerUserNameExists({
      username: userName,
      customerId: customerDetailsStore.customer.id!,
      customercontactId: contactId ?? 0,
    });
    const normalizedUserName = userName.toLowerCase();
    const duplicateUserName = customerDetailsStore.customer.contacts.some(
      (customerContact) =>
        customerContact.id !== contactId &&
        customerContact.userName?.trim().toLowerCase() === normalizedUserName,
    );
    const error =
      (res && res.isSuccess && Boolean(res.value)) || duplicateUserName
        ? `${userName} already exists in the system as a username. Please choose another one.`
        : null;
    const currentUserName = data().userName?.trim().toLowerCase() ?? '';

    if (currentUserName !== userName.toLowerCase()) {
      return true;
    }

    setDuplicateUserNameError(error);

    return !Boolean(error);
  };

  onMount(() => {
    if (Boolean(contact)) {
      setData('name', contact?.name ?? '');
      setData('type', contact?.type ?? '');
      setData('phone1', contact?.phone1 ?? '');
      setData('extension1', contact?.extension1 ?? null);
      setData('phone2', contact?.phone2 ?? '');
      setData('extension2', contact?.extension2 ?? null);
      setData('email', contact?.email ?? '');
      setData('emailVerified', contact?.emailVerified ?? false);
      setData('fax', contact?.fax ?? null);
      setData('userName', contact?.userName ?? null);
      setData('password', contact?.password ?? null);
      setData('phoneVerified', contact?.phoneVerified ?? false);
      setData('createdDate', contact?.createdDate ?? '');
      if (contactSource() === 'customer') {
        setData(
          'uniqueId',
          (contact as CustomerContactT | null)?.uniqueId ?? -1,
        );
      }

      setData('objectType', contact?.objectType ?? '');
      setData('verifiedDisabled', contact?.verifiedDisabled ?? false);
      setData('verificationStatus', contact?.verificationStatus ?? 0);
      setData('id', contact?.id ?? undefined);
      setData('mailerEmails', contact?.mailerEmails ?? false);
      setData('operationType', contact?.operationType ?? 'None');
    }
  });
  const handleFormSubmit = async (values: ContactFormValues) => {
    if (isReadOnly()) return;
    if (
      props.initiatePath === 'CustomerDetails' &&
      isLoginContactType(values.type)
    ) {
      handleLoginName.cancel();
      setIsUserNameLoading(true);
      try {
        const isLoginNameAvailable = await validateLoginNameAvailability(
          values.userName,
          values.id,
        );
        if (!isLoginNameAvailable) return;
      } finally {
        setIsUserNameLoading(false);
      }
    }

    const payload = { ...values };

    const removeProperties = (propsToRemove: string[]) => {
      propsToRemove.forEach(
        (prop) => delete payload[prop as keyof ContactFormValues],
      );
    };

    switch (props.initiatePath) {
      case 'OrderDetails':
        removeProperties([
          'uniqueId',
          'createdDate',
          'objectType',
          'verifiedDisabled',
          'verificationStatus',
          'phone',
          'mailerEmails',
        ]);
        payload.objectId = carrierStore.carrier[orderStore.activeTab.index].id;
        break;
      case 'CustomerDetails':
        removeProperties([
          'cpn',
          'phoneVerified',
          'emailVerified',
          'verifiedDisabled',
          'verificationStatus',
          'createdDate',
        ]);
        payload.phone = values.phone1;
        payload.objectType = 'Customer';
        break;
      case 'FactoringCompanyDetails':
        removeProperties([
          'cpn',
          'phoneVerified',
          'emailVerified',
          'verifiedDisabled',
          'verificationStatus',
          'createdDate',
          'uniqueId',
          'mailerEmails',
        ]);
        payload.phone = values.phone1;
        payload.objectType = 'Customer';
        break;
      case 'CarrierDetails':
        removeProperties(['cpn', 'userName', 'password', 'objectId', 'phone']);
        payload.verificationStatus =
          values.phoneVerified || values.emailVerified ? 1 : 0;
        payload.objectType = 'Carrier';
        break;

      case 'ProspectDetails':
        removeProperties([
          'cpn',
          'phoneVerified',
          'emailVerified',
          'verifiedDisabled',
          'verificationStatus',
          'createdDate',
          'uniqueId',
          'mailerEmails',
          'userName',
          'password',
          'objectType',
        ]);
        payload.phone = values.phone1;
        payload.objectType = 'Prospect';
        break;
      default:
        break;
    }

    try {
      setIsSubmitting(true);
      await props.onSubmit(payload);
      closeModal(props.modalId);
    } catch (error) {
      handleToast(ToastType.Error, 'Failed to Add New Contact');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getAddressCard = (contact: ExistingContactObj) => {
    return (
      <Grid item size={12} class={classes.addressCard}>
        <AddressCard
          contact={contact}
          type={contact.type}
          mode={contact.type === 'Billing' ? 'warning' : 'info'}
          showType={true}
          iconColor={'#2F6C9D'}
        />
      </Grid>
    );
  };

  const userCanMarkVerified = () => {
    return userHasRole('Carrier Relations');
  };

  const onCancelBtnTap = () => {
    closeModal(props.modalId);
    props.handleClose && props.handleClose();
  };

  const gridVal = props.initiatePath === 'OrderDetails' ? 6 : 12;

  const handleLoginName = debounce(async (value: string) => {
    setIsUserNameLoading(true);
    try {
      await validateLoginNameAvailability(value, data().id);
    } finally {
      setIsUserNameLoading(false);
    }
  }, 500);

  const handleContactTypeChange = (type: string) => {
    setData('type', type);

    if (!isLoginContactType(type)) {
      handleLoginName.cancel();
      setDuplicateUserNameError(null);
      setData('userName', null);
      setData('password', null);
    }
  };

  const customerOrCarrierTypeMenuItems = () => {
    if (props.initiatePath === 'CustomerDetails') {
      if (contact && (Boolean(contact.userName) || Boolean(contact.password))) {
        return CustomerTypes.filter(
          (type) => type === 'Billing' || type === 'Freight',
        );
      }
      return CustomerTypes;
    }
    if (props.initiatePath === 'ProspectDetails') {
      return CustomerTypes;
    }
    if (props.initiatePath === 'FactoringCompanyDetails') {
      return ['Billing', 'Payments', 'Remittance', 'Other'];
    }
    return CarrierTypes;
  };

  const handleTogglePasswordVisibility = (event: {
    preventDefault: () => void;
  }) => {
    event.preventDefault();
    setShowPassword(!showPassword());
  };

  return (
    <form ref={form} class={classes.rowFormGrid}>
      <Box style={{ 'flex-grow': 1 }}>
        <Show when={isUserNameLoading()}>
          <Box class="absolute top-[50%] left-[50%] z-50">
            <AtgLoader />
          </Box>
        </Show>
        <Grid container item size={12} spacing={4} class="mb-6!">
          <Grid
            item
            size={{
              xs: 12,
              lg: props.initiatePath === 'OrderDetails' ? 6 : 12,
            }}
          >
            <Notification
              type="info"
              text="Please add/edit contact information below. You can assign multiple billing or freight contacts."
            />
          </Grid>
          {props.initiatePath === 'OrderDetails' && (
            <Grid item size={{ xs: 12, lg: 6 }}>
              <Notification type="info" text="Existing contacts" />
            </Grid>
          )}
        </Grid>
        <Grid container spacing={4} class="px-1">
          <Grid item size={{ xs: 12, lg: gridVal }}>
            <Grid item container spacing={3}>
              <Grid item size={12}>
                <TextField
                  label="Name"
                  error={errors().name}
                  required
                  value={data().name}
                  onChange={(e: unknown) => {
                    setData('name', normalizeWhitespace(e as string));
                  }}
                  maxLength={50}
                  disabled={isReadOnly()}
                  dataTestId={'name-textInput'}
                />
              </Grid>
              <Grid item size={12}>
                <SelectInput
                  label="Type"
                  placeholder="Select Type"
                  menuItems={customerOrCarrierTypeMenuItems().map((t) => ({
                    label: t,
                    value: t,
                  }))}
                  value={data().type}
                  error={((): string | null => {
                    const err = errors().type;
                    if (err == null) return null;
                    if (Array.isArray(err)) return err[0] ?? null;
                    return typeof err === 'string' ? err : null;
                  })()}
                  onChange={(val) => handleContactTypeChange(val)}
                  disabled={isReadOnly()}
                  dataTestId={formatDataTestId(
                    'Add contact type',
                    'selectInput',
                  )}
                />
              </Grid>
              <Grid
                item
                size={{
                  xs: 12,
                  sm:
                    props.initiatePath === 'CustomerDetails' ||
                    props.initiatePath === 'FactoringCompanyDetails' ||
                    (props.useVerified ?? false) == false
                      ? 9
                      : 6,
                }}
              >
                <PhoneInput
                  label="Phone Number"
                  value={data().phone1}
                  onChange={(e) => setData('phone1', e)}
                  error={errors().phone1}
                  disabled={
                    isReadOnly() ||
                    (!userCanMarkVerified() && data().phoneVerified)
                  }
                  dataTestId={'phone-number-phoneInput'}
                  maxDigits={contactPhoneMaxDigits()}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 3 }}>
                <TextField
                  label="Ext1."
                  type="number"
                  value={data().extension1 ?? ''}
                  onChange={(e: unknown) => setData('extension1', e as string)}
                  error={errors().extension1}
                  disabled={
                    isReadOnly() ||
                    (!userCanMarkVerified() && data().phoneVerified)
                  }
                  maxLength={
                    props.initiatePath === 'CustomerDetails' ||
                    props.initiatePath === 'ProspectDetails'
                      ? 4
                      : 7
                  }
                  dataTestId={'ext1-textInput'}
                />
              </Grid>
              {props.initiatePath !== 'CustomerDetails' &&
                props.initiatePath !== 'FactoringCompanyDetails' &&
                (props.useVerified ?? false) && (
                  <Grid item size={{ xs: 6, lg: 3 }}>
                    <Checkbox
                      label="Verified?"
                      name="phoneVerified"
                      checked={data().phoneVerified}
                      onChange={(e) => setData('phoneVerified', e)}
                      disabled={isReadOnly() || !userCanMarkVerified()}
                      dataTestId={'phone-verified-checkboxInput'}
                      size="xs"
                      color="info"
                      class="ml-[-9px]"
                    />
                  </Grid>
                )}
              <Grid item size={{ xs: 9, lg: 9 }}>
                <PhoneInput
                  label="Alt"
                  value={data().phone2}
                  onChange={(e) => setData('phone2', e)}
                  error={errors().phone2}
                  disabled={isReadOnly()}
                  dataTestId={'alt-phone-phoneInput'}
                  maxDigits={contactPhoneMaxDigits()}
                />
              </Grid>
              <Grid item size={3}>
                <TextField
                  label="Ext2."
                  type="number"
                  error={errors().extension2 ?? ''}
                  value={data().extension2 ?? ''}
                  disabled={isReadOnly()}
                  onChange={(e: unknown) => setData('extension2', e as string)}
                  maxLength={
                    props.initiatePath === 'CustomerDetails' ||
                    props.initiatePath === 'ProspectDetails'
                      ? 4
                      : 7
                  }
                  dataTestId={'ext2-textInput'}
                />
              </Grid>
              <Grid
                item
                size={{
                  xs: 12,
                  lg:
                    props.initiatePath === 'CustomerDetails' ||
                    props.initiatePath === 'FactoringCompanyDetails' ||
                    (props.useVerified ?? false) == false
                      ? 12
                      : 9,
                }}
              >
                <TextField
                  label="Email"
                  type="email"
                  disabled={
                    isReadOnly() ||
                    (!userCanMarkVerified() && data().emailVerified)
                  }
                  value={data().email}
                  onChange={(val: string) => {
                    if (!val) {
                      setData('mailerEmails', false);
                    }
                    setData('email', val);
                  }}
                  autocomplete={'off'}
                  error={errors().email}
                  dataTestId={'email-textInput'}
                />
              </Grid>
              {props.initiatePath !== 'CustomerDetails' &&
                props.initiatePath !== 'FactoringCompanyDetails' &&
                (props.useVerified ?? false) && (
                  <Grid item size={3}>
                    <Checkbox
                      label="Verified?"
                      checked={data().emailVerified}
                      onChange={(e) => setData('emailVerified', e)}
                      disabled={isReadOnly() || !userCanMarkVerified()}
                      dataTestId={'email-verified-checkboxInput'}
                      size="xs"
                      color="info"
                      class="ml-[-9px]"
                    />
                  </Grid>
                )}
              <Grid item size={12}>
                <PhoneInput
                  label="Fax"
                  value={data().fax ?? ''}
                  onChange={(e) => setData('fax', e)}
                  error={errors().fax}
                  disabled={isReadOnly()}
                  dataTestId={'fax-phoneInput'}
                />
              </Grid>
              {['CarrierDetails', 'OrderDetails'].includes(
                props.initiatePath,
              ) && (
                <Show when={data().email}>
                  <Grid item size={12}>
                    <Checkbox
                      label="Receive Mailer Emails"
                      checked={data().mailerEmails}
                      onChange={(e) => setData('mailerEmails', e)}
                      disabled={isReadOnly()}
                      dataTestId={'receive-mailer-emails-checkboxInput'}
                      size="xs"
                      color="info"
                      class="ml-[-9px]"
                    />
                  </Grid>
                </Show>
              )}
              {['CustomerDetails'].includes(props.initiatePath) &&
                ['Billing', 'Freight'].includes(data().type) && (
                  <>
                    <Grid item size={12}>
                      <Divider />
                    </Grid>
                    <Grid item size={6}>
                      <TextField
                        label="Login Name"
                        value={data().userName ?? ''}
                        onChange={(value: unknown) => {
                          const userName = (value as string).trimStart();
                          setData('userName', userName);
                          setDuplicateUserNameError(null);
                          if (!userName.trim()) {
                            handleLoginName.cancel();
                            return;
                          }
                          void handleLoginName(userName);
                        }}
                        error={errors().userName || duplicateUserNameError()}
                        dataTestId={'login-name-textInput'}
                        autocomplete="off"
                      />
                    </Grid>
                    <Grid item size={6}>
                      <TextField
                        type={showPassword() ? 'text' : 'password'}
                        label="Password"
                        value={data().password ?? ''}
                        onChange={(e: unknown) =>
                          setData('password', e as string)
                        }
                        endIcon={
                          <IconButton
                            dataTestId={formatDataTestId(
                              'Password Visibility',
                              'button',
                            )}
                            on:click={handleTogglePasswordVisibility}
                            style={{ padding: 0 }}
                          >
                            {showPassword() ? <EyeIcon /> : <EyeOffIcon />}
                          </IconButton>
                        }
                        autocomplete="new-password"
                        error={errors().password}
                        dataTestId={'password-textInput'}
                      />
                    </Grid>
                  </>
                )}
            </Grid>
          </Grid>
          <Grid item size={{ xs: 12, lg: 6 }}>
            <Grid item container spacing={4}>
              {props.initiatePath === 'OrderDetails' && (
                <Grid item size={12}>
                  <TextField
                    dataTestId={'corporate-phone-number1-textInput'}
                    label="Corporate Phone Number1"
                    disabled
                    value={data().cpn}
                    onChange={(e: unknown) => setData('cpn', e as string)}
                  />
                </Grid>
              )}
              <Grid size={12} item>
                <Grid
                  item
                  spacing={1}
                  container
                  maxHeight={'450px'}
                  overflow="auto"
                >
                  {carrierStore.carrier[orderStore.activeTab.index]?.contacts
                    .length &&
                    carrierStore.carrier[
                      orderStore.activeTab.index
                    ]?.contacts.map((contact) =>
                      getAddressCard(contact as unknown as ExistingContactObj),
                    )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid
            item
            size={12}
            class={`${classes.buttonFooterGrid} flex gap-2 items-center justify-end`}
          >
            <Show when={!isReadOnly()}>
              <Button
                label="Save"
                isLoading={isSubmitting()}
                disabled={
                  isSubmitting() ||
                  isUserNameLoading() ||
                  Boolean(duplicateUserNameError())
                }
                type="submit"
                variant="contained"
                dataTestId={formatDataTestId('Save', 'button')}
              />
            </Show>
            <Button
              label={isReadOnly() ? 'Close' : 'Cancel'}
              isLoading={isSubmitting()}
              disabled={isSubmitting()}
              variant="text"
              onClick={onCancelBtnTap}
              dataTestId={formatDataTestId('Cancel', 'button')}
            />
          </Grid>
        </Grid>
      </Box>
    </form>
  );
}

export default AddNewContact;
